import { setYear } from 'date-fns'

import FinancialRecord from '#calculator/financialRecords/FinancialRecord'
import generateId from '#calculator/financialRecords/generateId'
import type MemberService from '#profile/MemberService'

import type { AgeRange } from './resolveDateRangeBounds'
import {
  type EffectiveOverrides,
  effectiveRecordOverrideKey,
} from './resolveEffectiveOverrides'

/**
 * The record id a submission-scoped override targets.
 *
 * Overrides are stored against a submission id, but records are matched by record
 * id — `FinancialRecord` carries no submission id. The root record's id is
 * `generateId(submissionId, null, 0)`, a pure function of the submission id with
 * no positional component, so this mapping is exact and stable.
 */
export const rootRecordIdFor = (submissionId: string): string =>
  generateId(submissionId, null, 0)

type RecordChanges = {
  annualValue: number
  startAt: Date
  endAt: Date
}

const withChanges = (
  record: FinancialRecord,
  { annualValue, startAt, endAt }: RecordChanges
): FinancialRecord =>
  // TODO: simplify once FinancialRecord is replaced with a plain JS object.
  // Mirrors `adjustments/adjustRecords.ts`, which rebuilds the same way.
  new FinancialRecord(
    {
      id: record.id,
      name: record.name,
      ownerId: record.owner.id,
      finCategoryKey: record.finCategory.key,
      startAt,
      endAt,
      annualValue,
      valuePart: record.valuePart,
      recurringValue: record.recurringValue,
      // Round-trips the custom rate only when one was actually set. `growthRate`
      // is `customGrowthRate ?? finCategory.rate`, so passing the category rate
      // back as a custom rate is indistinguishable today — this keeps the payload
      // honest rather than fixing a live bug.
      customRate:
        record.growthRate !== record.finCategory.rate
          ? record.growthRate
          : null,
      parentId: record.parentId,
      realEstateRegion: null,
      realEstateTaxRate: null,
      questId: record.questId,
      t_fromAssetId: record.t_fromAssetId,
      t_toAssetId: record.t_toAssetId,
    },
    record.finCategory,
    record.owner
  )

/**
 * The date a whole-age override lands on.
 *
 * Only the YEAR moves. An override stores an age, so the month and day are none
 * of the quest's business — rewriting them would shift every overridden record to
 * whatever date the conversion happened to pick.
 */
const atAge = (date: Date, age: number, owner: MemberService): Date =>
  setYear(date, owner.getYearForAge(age))

/**
 * Substitutes quest overrides into already-parsed records.
 *
 * This is the record-level replacement for substituting raw answers before the
 * parse (ZL-815). Overriding the parsed value means a quest never has to know how
 * the plan stored the figure — `moneyWithPeriod` annualisation has already
 * happened — and the survey YAML needs no per-question opt-in declaration.
 *
 * The trade-off is deliberate and worth knowing: an override lands on exactly one
 * record. Expressions are evaluated against `{ answers, vars }` only, so nothing
 * derived from the same answer recomputes. A rent record (one answer, one record)
 * behaves identically to the old answer substitution; a home purchase whose price
 * feeds an asset record, a mortgage child and property tax does not — the
 * untargeted records keep their original figures.
 *
 * Records with no override are returned by reference, so an unaffected projection
 * keeps its object identity and downstream memoisation holds.
 *
 * TODO(ZL-815): a window override does not touch `recurringValue`, which carries
 * its own instalment schedule from build time. Shortening the window of a record
 * that has one would leave the two disagreeing about how long the payment runs.
 * Not reachable today — only ROOT records are addressable and the recurring cases
 * (a mortgage under a home purchase) are children — but it has to be settled
 * before child records become targetable.
 */
const applyRecordOverrides = (
  records: readonly FinancialRecord[],
  resolved: EffectiveOverrides
): readonly FinancialRecord[] => {
  if (resolved.recordOverrides.size === 0) return records

  // Record id -> change, so matching is one lookup per record rather than a scan
  // of the override map. Keyed separately per field because the two are
  // independent: one quest may own a record's amount while another owns its
  // window.
  const annualValueByRecordId = new Map<string, number>()
  const windowByRecordId = new Map<string, AgeRange>()
  resolved.recordOverrides.forEach(override => {
    const recordId = rootRecordIdFor(override.submissionId)
    if (override.field === 'annualValue') {
      annualValueByRecordId.set(recordId, override.value)
    } else {
      windowByRecordId.set(recordId, override.value)
    }
  })

  return records.map(record => {
    const annualValue =
      annualValueByRecordId.get(record.id) ?? record.annualValue
    const window = windowByRecordId.get(record.id)
    const { owner } = record

    const startAt =
      window?.startAge === undefined
        ? record.startAt
        : atAge(record.startAt, window.startAge, owner)
    const requestedEnd =
      window?.endAge === undefined
        ? record.endAt
        : atAge(record.endAt, window.endAge, owner)
    // A stored window can go stale — the user ends the rent at 45, then moves the
    // plan so the record starts at 50 — and an inverted record is not something
    // the engine should ever be handed. The end yields, because it is the pole a
    // quest most often moves and because the alternative is dropping the
    // override entirely and silently doing nothing.
    const endAt =
      requestedEnd.getFullYear() < startAt.getFullYear()
        ? setYear(requestedEnd, startAt.getFullYear())
        : requestedEnd

    const unchanged =
      annualValue === record.annualValue &&
      startAt.getTime() === record.startAt.getTime() &&
      endAt.getTime() === record.endAt.getTime()
    if (unchanged) return record

    return withChanges(record, { annualValue, startAt, endAt })
  })
}

export const recordOverrideKeyFor = effectiveRecordOverrideKey

export default applyRecordOverrides
